/**
 * Pricing Engine™ v2 — the ONLY place a price is calculated.
 *
 *   MRP → campaign → coupon → scholarship → partner → institution → regional
 *       → currency → taxes → gateway → final payable amount
 *
 * Nothing outside this directory may do pricing arithmetic. Client and server
 * both consume this file (the server copy is generated by
 * `scripts/sync-pricing-engine.mjs`), so the UI can never quote a number the
 * gateway does not charge.
 */

import { resolveCampaign, autoApplyCampaigns } from "./campaign-engine";
import {
  fromDatabaseRow,
  isPriceableInRegion,
  resolveRuleForRegion,
  validateCouponRule,
  type CouponRowLike,
} from "./coupon-engine";
import {
  DEFAULT_REGION,
  FULL_DISCOUNT_ALLOWLIST,
  PARTNERS,
  PRICING_VERSION,
  REGIONS,
  SCHOLARSHIPS,
} from "./pricing-config";
import { selectWinner } from "./pricing-policy";
import type {
  AppliedRule,
  DiscountLayer,
  DiscountRule,
  PricingEvent,
  PricingEventName,
  PricingRequest,
  PricingResult,
  RegionCode,
} from "./pricing-types";
import {
  discountAmount,
  normaliseCode,
  round2,
  roundMoney,

  taxAmount,
  toMinorUnits,
} from "./pricing-utils";

export { PRICING_VERSION };

function toDate(value: PricingRequest["now"]): Date {
  if (!value) return new Date();
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function region(code?: RegionCode) {
  const r = REGIONS[code ?? DEFAULT_REGION];
  return r && r.active !== false ? r : REGIONS[DEFAULT_REGION];
}

/**
 * Price one order.
 *
 * Deterministic: same request plus same `now` always yields the same result.
 */
export function calculatePrice(request: PricingRequest = {}): PricingResult {
  const now = toDate(request.now);
  const reg = region(request.region);
  const quantity = Math.max(1, Math.floor(Number(request.quantity) || 1));
  const listPrice = round2(reg.listPrice);
  const appliedRules: AppliedRule[] = [];

  const candidates: Array<{ rule: DiscountRule; discount: number }> = [];
  let couponError: PricingResult["couponError"] = null;

  const allowed = (layer: DiscountLayer) => reg.allowedLayers.includes(layer);

  const note = (
    rule: DiscountRule,
    discount: number,
    applied: boolean,
    reason: string,
  ) => {
    appliedRules.push({
      layer: rule.layer,
      id: rule.id,
      label: rule.label,
      type: rule.type,
      value: rule.value,
      discount,
      applied,
      reason,
    });
  };

  // ---- 1. Campaign ------------------------------------------------------
  const requested = resolveCampaign(request.campaignId, now, reg.code);
  const autos = autoApplyCampaigns(now, reg.code);
  for (const configured of requested ? [requested] : autos) {
    if (!allowed("campaign")) {
      note(configured, 0, false, `campaigns are not enabled for region ${reg.code}`);
      continue;
    }
    if (!isPriceableInRegion(configured, reg.code)) {
      note(configured, 0, false, `no ${reg.currency} value declared for region ${reg.code}`);
      continue;
    }
    const campaign = resolveRuleForRegion(configured, reg.code);
    candidates.push({ rule: campaign, discount: discountAmount(campaign.type, campaign.value, listPrice) });
  }

  // ---- 2/3/4. Coupon (may resolve into campaign / scholarship / partner) --
  const typedCode = normaliseCode(request.couponCode);
  if (typedCode || request.couponOverride) {
    const override = request.couponOverride ?? null;
    const validation = validateCouponRule({
      code: override?.id ?? typedCode,
      rule: override,
      region: reg.code,
      now,
      isAdmin: request.isAdmin === true,
      allowedLayers: reg.allowedLayers,
    });

    if (validation.ok && validation.rule) {
      const rule = validation.rule;
      const discount = discountAmount(rule.type, rule.value, listPrice);
      if (round2(listPrice - discount) <= 0 && !FULL_DISCOUNT_ALLOWLIST.includes(rule.id)) {
        couponError = {
          reason: "full_discount_retired",
          message:
            "Free-access codes have been retired. Please use the personal invite link sent to your email, or contact us at hello@wekit.ai.",
        };
        note(rule, discount, false, "zero-payable codes are retired");
      } else {
        candidates.push({ rule, discount });
      }
    } else {
      couponError = validation.rejection;
      note(
        {
          id: override?.id ?? (typedCode || "(empty)"),
          label: override?.label ?? (typedCode || "(empty)"),
          layer: "coupon",
          type: "none",
          value: 0,
        },
        0,
        false,
        validation.rejection?.reason ?? "rejected",
      );
    }
  }

  // ---- Scholarship requested directly (not via a code) -------------------
  if (request.scholarshipId) {
    const configured = SCHOLARSHIPS.find((x) => x.id === request.scholarshipId && x.active !== false);
    const s = configured ? resolveRuleForRegion(configured, reg.code) : null;
    if (configured && s && allowed("scholarship") && isPriceableInRegion(configured, reg.code)) {
      candidates.push({ rule: s, discount: discountAmount(s.type, s.value, listPrice) });
    } else if (s) {
      note(s, 0, false, `scholarships are not enabled for region ${reg.code}`);
    }
  }

  // ---- Partner / institution rate ---------------------------------------
  const partnerKey = request.partnerId ?? request.institutionId ?? null;
  if (partnerKey) {
    const configured = PARTNERS.find((x) => x.id === partnerKey && x.active !== false);
    const p = configured ? resolveRuleForRegion(configured, reg.code) : null;
    if (configured && p && allowed("partner") && isPriceableInRegion(configured, reg.code)) {
      candidates.push({ rule: p, discount: discountAmount(p.type, p.value, listPrice) });
    } else if (p) {
      note(p, 0, false, `partner pricing is not enabled for region ${reg.code}`);
    }
  }

  // ---- Account entitlements from the Access & Benefits Engine ------------
  for (const raw of request.entitlements ?? []) {
    if (!raw) continue;
    const rule = resolveRuleForRegion(raw, reg.code);
    if (!allowed(rule.layer)) {
      note(rule, 0, false, `${rule.layer} benefits are not enabled for region ${reg.code}`);
      continue;
    }
    if (!isPriceableInRegion(raw, reg.code)) {
      note(rule, 0, false, `benefit ${rule.id} is not priceable in ${reg.code}`);
      continue;
    }
    candidates.push({ rule, discount: discountAmount(rule.type, rule.value, listPrice) });
  }

  // ---- Precedence: exactly one layer wins --------------------------------
  const { winner, losers } = selectWinner(candidates);
  for (const l of losers) note(l.rule, l.discount, false, l.reason);
  if (winner) note(winner.rule, winner.discount, true, "applied under Pricing Policy v2");

  const byLayer: Record<string, number> = {
    campaign: 0,
    scholarship: 0,
    coupon: 0,
    partner: 0,
    institution: 0,
    regional: 0,
  };
  if (winner) byLayer[winner.rule.layer] = winner.discount;

  // Every published number is rounded in the region's own currency — India
  // never shows paise, so the discount, subtotal, tax and total all land on
  // whole rupees and still reconcile with each other.
  const totalDiscount = roundMoney(winner?.discount ?? 0, reg.currency);
  const perSeatSubtotal = roundMoney(Math.max(0, listPrice - totalDiscount), reg.currency);
  const subtotal = roundMoney(perSeatSubtotal * quantity, reg.currency);
  const taxes = roundMoney(
    taxAmount(subtotal, reg.taxRatePercent, reg.taxInclusive),
    reg.currency,
  );
  const finalAmount = roundMoney(subtotal + taxes, reg.currency);

  const gateway = request.gateway ?? reg.gateway;

  return {
    pricingVersion: PRICING_VERSION,
    region: reg.code,
    currency: reg.currency,
    gatewayCurrency: reg.currency,
    gateway,
    quantity,

    listPrice,
    campaignDiscount: roundMoney(byLayer.campaign, reg.currency),
    couponDiscount: roundMoney(byLayer.coupon, reg.currency),
    scholarshipDiscount: roundMoney(byLayer.scholarship, reg.currency),
    partnerDiscount: roundMoney(byLayer.partner + byLayer.institution, reg.currency),

    regionalDiscount: byLayer.regional,
    totalDiscount,

    subtotal,
    taxes,
    taxLabel: reg.taxLabel,
    taxInclusive: reg.taxInclusive,
    finalAmount,
    finalAmountMinorUnits: toMinorUnits(finalAmount),

    appliedRules,
    couponError,
    auditTrail: {
      timestamp: now.toISOString(),
      pricingVersion: PRICING_VERSION,
      userId: request.userId ?? null,
      region: reg.code,
      currency: reg.currency,
      gateway,
      listPrice,
      couponCode: winner && winner.rule.id === typedCode ? typedCode : (typedCode || null),
      campaignId: winner?.rule.layer === "campaign" ? winner.rule.id : null,
      scholarshipId: winner?.rule.layer === "scholarship" ? winner.rule.id : null,
      partnerId:
        winner?.rule.layer === "partner" || winner?.rule.layer === "institution"
          ? winner.rule.id
          : null,
      totalDiscount,
      taxes,
      finalAmount,
      appliedRuleIds: appliedRules.filter((r) => r.applied).map((r) => r.id),
    },
  };
}

/**
 * Price using a `coupons` table row — the server path. The database stays
 * authoritative for redemption limits and admin edits; the maths stays here.
 */
export function calculatePriceWithCouponRow(
  row: CouponRowLike | null,
  request: PricingRequest = {},
): PricingResult {
  return calculatePrice({
    ...request,
    couponOverride: row ? fromDatabaseRow(row) : null,
    couponCode: row ? normaliseCode(row.code) : request.couponCode,
  });
}

/**
 * Back-compatible helper for the original two-field contract. Existing edge
 * functions keep working unchanged; the numbers now come from the engine.
 */
export function computeCouponPrice(
  discountType: string,
  discountValue: number,
  opts: { region?: RegionCode } = {},
): { final: number; discount: number } {
  const reg = region(opts.region);
  const rawDiscount = discountAmount(discountType, discountValue, reg.listPrice);
  const discount = roundMoney(rawDiscount, reg.currency);
  return {
    final: roundMoney(Math.max(0, reg.listPrice - discount), reg.currency),
    discount,
  };
}


// ---------------------------------------------------------------------------
// Event hooks. No provider is wired in here by design — a consumer subscribes
// and forwards to whatever analytics sink it already uses.
// ---------------------------------------------------------------------------

type Listener = (event: PricingEvent) => void;
const listeners = new Set<Listener>();

export function onPricingEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitPricingEvent(
  name: PricingEventName,
  payload: Record<string, unknown> = {},
): PricingEvent {
  const event: PricingEvent = {
    name,
    at: new Date().toISOString(),
    pricingVersion: PRICING_VERSION,
    payload,
  };
  for (const l of listeners) {
    try {
      l(event);
    } catch {
      // A broken listener must never break a checkout.
    }
  }
  return event;
}

/** Derive the events a calculation implies, without emitting them. */
export function pricingEventsFor(result: PricingResult): PricingEventName[] {
  const events: PricingEventName[] = [];
  const winner = result.appliedRules.find((r) => r.applied);
  if (result.couponError) {
    events.push(
      result.couponError.reason === "expired" ? "coupon_expired" : "coupon_failed",
    );
  }
  if (winner) {
    if (winner.layer === "campaign") events.push("campaign_applied");
    else if (winner.layer === "scholarship") events.push("scholarship_applied");
    else if (winner.layer === "partner" || winner.layer === "institution")
      events.push("partner_discount_applied");
    else events.push("coupon_applied");
  }
  return events;
}
